import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useZustandAuth } from '../hooks/useZustandAuth';
import { useAuthStore } from '../stores/authStore';
import { notify } from '../utils/notifications';
import styled from 'styled-components';
export const PermissionWrapper = ({ permission, fallback = null, children, showFallback = false }) => {
    const { hasPermission } = useZustandAuth();
    const hasAccess = hasPermission(permission);
    if (!hasAccess) {
        return showFallback ? _jsx(_Fragment, { children: fallback }) : null;
    }
    return _jsx(_Fragment, { children: children });
};
// HOC for page protection
export const withPermissionGuard = (WrappedComponent, requiredPermission, fallbackComponent) => {
    const PermissionGuardedComponent = (props) => {
        const { hasPermission, isAuthenticated, isLoading, user, permissions } = useZustandAuth();
        // Override check - if Zustand shows authenticated, use that
        const zustandAuth = useAuthStore();
        const actuallyAuthenticated = zustandAuth.isAuthenticated;
        const actuallyHasPermission = zustandAuth.hasPermission(requiredPermission);
        // Debug logging for permission guard
        console.log('🛡️ Permission Guard Check:', {
            requiredPermission,
            zustandAuthenticated: actuallyAuthenticated,
            zustandHasPermission: actuallyHasPermission,
            zustandUserRole: zustandAuth.user?.role,
            zustandTotalPermissions: zustandAuth.permissions.length,
            hookAuthenticated: isAuthenticated,
            hookHasPermission: hasPermission(requiredPermission),
            hookTotalPermissions: permissions?.length || 0
        });
        if (zustandAuth.isLoading) {
            return _jsx(LoadingScreen, {});
        }
        if (!actuallyAuthenticated) {
            return _jsx(LoginRequiredScreen, {});
        }
        if (!actuallyHasPermission) {
            console.log('❌ Permission Guard - Access Denied:', {
                requiredPermission,
                zustandPermissions: zustandAuth.permissions,
                userRole: zustandAuth.user?.role
            });
            if (fallbackComponent) {
                const FallbackComponent = fallbackComponent;
                return _jsx(FallbackComponent, {});
            }
            return _jsx(AccessDeniedScreen, { requiredPermission: requiredPermission });
        }
        console.log('✅ Permission Guard - Access Granted for:', requiredPermission);
        return _jsx(WrappedComponent, { ...props });
    };
    PermissionGuardedComponent.displayName = `withPermissionGuard(${WrappedComponent.displayName || WrappedComponent.name})`;
    return PermissionGuardedComponent;
};
export const RoleGuard = ({ minimumRole, children, fallback = null }) => {
    const { user } = useZustandAuth();
    if (!user) {
        return _jsx(_Fragment, { children: fallback });
    }
    // Check if user's role level meets minimum requirement
    const userRoleLevel = user.roleLevel;
    const requiredRoleLevel = parseInt(minimumRole);
    if (userRoleLevel < requiredRoleLevel) {
        return _jsx(_Fragment, { children: fallback });
    }
    return _jsx(_Fragment, { children: children });
};
// Styled components for access control screens
const AccessScreen = styled.div `
  min-height: calc(100vh - 140px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  background: #000000;
  color: #FFFFFF;
`;
const AccessTitle = styled.h1 `
  font-family: 'Adamina', serif;
  font-size: 36px;
  margin-bottom: 24px;
  color: #FFFFFF;
`;
const AccessMessage = styled.p `
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  color: #A4A4A4;
  margin-bottom: 32px;
  max-width: 500px;
  line-height: 1.6;
`;
const AccessButton = styled.button `
  background: transparent;
  color: #FFFFFF;
  border: 1px solid #FFFFFF;
  border-radius: 0;
  padding: 12px 32px;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #FFFFFF;
    color: #000000;
    transform: translateY(-2px);
  }
`;
// Loading screen component
export const LoadingScreen = () => (_jsxs(AccessScreen, { children: [_jsx(AccessTitle, { children: "Loading..." }), _jsx(AccessMessage, { children: "Verifying your access permissions..." })] }));
// Login required screen
export const LoginRequiredScreen = () => (_jsxs(AccessScreen, { children: [_jsx(AccessTitle, { children: "Authentication Required" }), _jsx(AccessMessage, { children: "Please log in to access this page. If you don't have an account, contact us to become a verified dealer." }), _jsxs("div", { style: { display: 'flex', gap: '16px' }, children: [_jsx(AccessButton, { onClick: () => window.location.href = '/login', children: "Login" }), _jsx(AccessButton, { onClick: () => window.location.href = '/contact', children: "Contact Us" })] })] }));
export const AccessDeniedScreen = ({ requiredPermission }) => {
    const { user, roleName } = useZustandAuth();
    return (_jsxs(AccessScreen, { children: [_jsx(AccessTitle, { children: "Access Restricted" }), _jsxs(AccessMessage, { children: ["Your current role (", roleName, ") does not have permission to access this resource.", requiredPermission && (_jsxs(_Fragment, { children: [_jsx("br", {}), _jsx("br", {}), "Required permission: ", _jsx("strong", { children: requiredPermission })] })), _jsx("br", {}), _jsx("br", {}), "Contact your account manager to request access or upgrade your dealer status."] }), _jsxs("div", { style: { display: 'flex', gap: '16px' }, children: [_jsx(AccessButton, { onClick: () => window.location.href = '/', children: "Go Home" }), _jsx(AccessButton, { onClick: () => window.location.href = '/contact', children: "Contact Support" })] })] }));
};
export const ComponentGuard = ({ permission, children, fallback = null, loader = null }) => {
    const { hasPermission, isLoading } = useZustandAuth();
    if (isLoading && loader) {
        return _jsx(_Fragment, { children: loader });
    }
    const hasAccess = hasPermission(permission);
    if (!hasAccess) {
        return _jsx(_Fragment, { children: fallback });
    }
    return _jsx(_Fragment, { children: children });
};
// Action guard hook
export const useActionGuard = (actionPermission) => {
    const { canPerformAction, user } = useZustandAuth();
    return {
        canPerform: canPerformAction(actionPermission),
        performAction: (action, onUnauthorized) => {
            if (canPerformAction(actionPermission)) {
                action();
            }
            else {
                console.warn(`Unauthorized action attempted: ${actionPermission} by user: ${user?.id}`);
                if (onUnauthorized) {
                    onUnauthorized();
                }
                else {
                    // Default unauthorized behavior
                    notify.warning('Access Denied', 'You do not have permission to perform this action.');
                }
            }
        }
    };
};
// Admin-only wrapper
export const AdminOnly = ({ children, fallback = null }) => (_jsx(RoleGuard, { minimumRole: "7", fallback: fallback, children: children }));
// Dealer-only wrapper
export const DealerOnly = ({ children, fallback = null }) => (_jsx(RoleGuard, { minimumRole: "4", fallback: fallback, children: children }));
// Super admin only wrapper
export const SuperAdminOnly = ({ children, fallback = null }) => (_jsx(RoleGuard, { minimumRole: "10", fallback: fallback, children: children }));
